import { useState, useEffect, useRef, useMemo } from 'react';
import { parseDate, isSameDay, getDayOfWeekMondayFirst } from '../utils/dateUtils';
import { useTranslation } from '../i18n';
import './DatePicker.scss';

/**
 * DatePicker - Mini calendar popup for selecting dates
 * @param {Object} props
 * @param {Date|string} props.value - Currently selected date
 * @param {Function} props.onChange - Callback when date is selected
 * @param {Function} props.onClose - Callback to close the picker
 * @param {Date|string} props.minDate - Optional minimum selectable date
 * @param {Date|string} props.maxDate - Optional maximum selectable date
 */
function DatePicker({ value, onChange, onClose, minDate, maxDate }) {
  const { t, locale } = useTranslation();
  const daysOfWeek = t('calendar.daysShort');
  
  const selectedDate = useMemo(() => parseDate(value), [value]);
  const minDateParsed = useMemo(() => minDate ? parseDate(minDate) : null, [minDate]);
  const maxDateParsed = useMemo(() => maxDate ? parseDate(maxDate) : null, [maxDate]);
  
  const [viewYear, setViewYear] = useState(selectedDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(selectedDate.getMonth());
  const containerRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        onClose();
      }
    };
    
    // Delay adding listener to prevent immediate close
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);
    
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Generate calendar grid for current view month
  const calendarDays = useMemo(() => {
    const days = [];
    const firstDay = new Date(viewYear, viewMonth, 1);
    const lastDay = new Date(viewYear, viewMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = getDayOfWeekMondayFirst(firstDay);
    
    // Add empty cells for days before the 1st
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push({ type: 'empty', key: `empty-${i}` });
    }
    
    // Add days of the month
    const today = new Date();
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(viewYear, viewMonth, day);
      const isToday = isSameDay(date, today);
      const isSelected = isSameDay(date, selectedDate);
      
      // Check if date is within allowed range
      let isDisabled = false;
      if (minDateParsed && date < minDateParsed) {
        // Allow same day
        if (!isSameDay(date, minDateParsed)) {
          isDisabled = true;
        }
      }
      if (maxDateParsed && date > maxDateParsed) {
        if (!isSameDay(date, maxDateParsed)) {
          isDisabled = true;
        }
      }
      
      days.push({
        type: 'day',
        day,
        date,
        isToday,
        isSelected,
        isDisabled,
        key: `day-${day}`
      });
    }
    
    return days;
  }, [viewYear, viewMonth, selectedDate, minDateParsed, maxDateParsed]);

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewYear(viewYear - 1);
      setViewMonth(11);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewYear(viewYear + 1);
      setViewMonth(0);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  const handleDayClick = (dayInfo) => {
    if (dayInfo.isDisabled) return;
    onChange(dayInfo.date);
    onClose();
  };

  const monthName = new Date(viewYear, viewMonth, 1).toLocaleDateString(locale, { 
    month: 'long', 
    year: 'numeric' 
  });

  return (
    <div className="date-picker" ref={containerRef}>
      <div className="date-picker__header">
        <button 
          type="button" 
          className="date-picker__nav" 
          onClick={handlePrevMonth}
        >
          {'\u2039'}
        </button>
        <span className="date-picker__month">{monthName}</span>
        <button 
          type="button" 
          className="date-picker__nav" 
          onClick={handleNextMonth}
        >
          {'\u203A'}
        </button>
      </div>
      
      <div className="date-picker__weekdays">
        {daysOfWeek.map(day => (
          <div key={day} className="date-picker__weekday">{day}</div>
        ))}
      </div>
      
      <div className="date-picker__days">
        {calendarDays.map(dayInfo => {
          if (dayInfo.type === 'empty') {
            return <div key={dayInfo.key} className="date-picker__day date-picker__day--empty" />;
          }
          
          const classNames = [
            'date-picker__day',
            dayInfo.isToday ? 'date-picker__day--today' : '',
            dayInfo.isSelected ? 'date-picker__day--selected' : '',
            dayInfo.isDisabled ? 'date-picker__day--disabled' : ''
          ].filter(Boolean).join(' ');
          
          return (
            <button
              key={dayInfo.key}
              type="button"
              className={classNames}
              onClick={() => handleDayClick(dayInfo)}
              disabled={dayInfo.isDisabled}
            >
              {dayInfo.day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default DatePicker;
